import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import useOpenState from '../../../../hooks/useOpenState';
import MessageBox from '../../../../util/messageBox';
import {Divider, FormLabel, Grid} from '@mui/material';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AddIcon from '@mui/icons-material/Add';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import _ from 'lodash';
import moment from 'moment';
import config from '../../../../app/config';
import useDict from '../../../../hooks/useDict';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import API from '../../../../app/api';
import Notes from '../../../../util/notes';
import useCliniciansAndNursesDict from '../../../../hooks/useCliniciansAndNursesDict';
import {addOrReplace, getPatientFlagValue} from '../../mainScreen/util/patientFlagsUtil';
import {getFacility} from '../formsUtil';
import {DEFAULT_STATE_CSM} from './csmFormUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: `${theme.palette.background.paper} !important`,
    },
    groupRoot: {
        marginBottom: 10,
        padding: 5,
        borderRadius: 5,
    },
    paper: {
        padding: 10,
        backgroundColor: theme.palette.background.default,
    },
    centerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    userLog: {
        fontSize: 11,
        fontStyle: 'italic',
        color: theme.palette.mode === 'light' ? '#4B4B4B' : '#818181',
    },
    comment: {
        fontSize: 15,
        padding: '5px 10px',
        backgroundColor: theme.palette.background.default,
        borderRadius: 10,
        marginTop: 5,
    },
    clinician: {
        marginBottom: 10,
    },
    row: {
        width: '100%',
        marginBottom: 10,
    },
    divider: {
        margin: '10px 0',
    },
}));

const BedRequestForm = (props) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    // User object
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    // Form state
    const [form, setForm] = useState(DEFAULT_STATE_CSM);
    const [saveData, setSaveData] = useState(false);
    const [isClearConfirmOpen, openClearConfirm, closeClearConfirm] = useOpenState(false, true);

    // Comment
    const [isCommentOpen, openComment, closeComment] = useOpenState(false, true);
    const [comment, setComment] = useState('');
    const type = useRef(null);

    // Dict
    const [nursesCliniciansList] = useCliniciansAndNursesDict('', !config?.dropdownsWithCliniciansAndNurses);
    const [cliniciansDict, cliniciansLoading] = useDict('Consultants', null, 'deactivated ne true and availableIn ne null');
    const [activityTreatment, activityTreatmentLoading] = useDict('ActivityTreatment', null, 'deactivated ne true');
    const [wards, setWards] = useState([]);

    useEffect(() => {
        const controller = new AbortController();
        if (props.isBB && props.patient?.csmContactedTime) {
            setForm(props.patient?.csmContactedTime);
        } else if (props.patient?.studies?.bedRequest) {
            setForm(props.patient?.studies?.bedRequest);
        } else {
            let clone = _.cloneDeep(DEFAULT_STATE_CSM);
            addUserChange(clone, 'requested');
            clone.requested = true;
            clone.requestedWhen = moment();
            setForm(clone);
        }

        API.getWards(controller.signal)
            .then(response => {
                if (response?.data?.__data) {
                    setWards(_.orderBy(response?.data?.__data, ['wardName'], ['asc']));
                }
            });

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (props.patient?.facility && form.anotherDestination === '' && props.patient?.studies?.bedReques?.anotherDestination === '') {
            setForm(prevState => ({
                ...prevState,
                anotherDestination: getFacility(props.patient?.facility),
            }));
        }
    }, [props.patient, form]);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons([
                <AgyleButton
                    key="delete"
                    disabled={props.disableSave}
                    onClick={openClearConfirm}
                    colour="primary"
                >
                    {t('Delete')}
                </AgyleButton>,
            ]);
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSubmit);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(!saveData || props.disableSave);
        }

        if (props.agyleDialogInsertButtons) {
            const buttons = [];
            if (!props.disableSave) {
                buttons.push(
                    <AgyleButton onClick={openClearConfirm} disabled={dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ONLY))}>
                        {t('Delete')}
                    </AgyleButton>,
                    <AgyleButton variant="contained" onClick={handleSubmit} disabled={dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_ONLY))}>
                        {t('Submit')}
                    </AgyleButton>,
                );
            }
            props.agyleDialogInsertButtons(buttons);
        }
    }, [form, saveData, props.disableSave]);

    const handleSubmit = () => {
        let data = _.cloneDeep(form);
        data.lastUser = userFullName;
        data.lastUserLogin = userObject?.userName;
        data.when = moment().toISOString();

        const objToSave = ([
            {
                name: 'studies.bedRequest',
                noteType: 'studies.bedRequest',
                value: data,
                noteToAdd: Notes.getBedRequestNote(data),
            },
        ]);

        if (data.sideRoom !== getPatientFlagValue(props.patient, 'sr')) {
            let flags = [];
            if (props.patient?.metadata?.patientFlags) {
                flags = _.cloneDeep(props.patient.metadata.patientFlags);
            }

            const flag = {
                name: 'sr',
                checked: data.sideRoom,
                by: userFullName,
                login: userObject?.userName,
                date: moment().toISOString(),
            };
            addOrReplace(flags, flag);
            objToSave.push({name: 'metadata.patientFlags', value: flags});
        }

        if (props.onSave) props.onSave(objToSave);
        if (props.onChange) props.onChange(objToSave);
        if (props.onClose) props.onClose();
    };

    const handleClearData = () => {
        setForm(DEFAULT_STATE_CSM);
        setSaveData(true);
        closeClearConfirm();
    };

    const addUserChange = (obj, field) => {
        obj[`${field}User`] = userFullName;
        obj[`${field}UserLogin`] = userObject?.userName;
        obj[`${field}Timestamp`] = moment().toISOString();
    };

    const handleAddComment = () => {
        let clone = _.cloneDeep(form);
        clone[`${type.current}Note`] = comment;
        addUserChange(clone, type.current);
        setForm(clone);
        setSaveData(true);

        setComment('');
        type.current = null;
        closeComment();
    };

    const handleCloseComment = (e, reason) => {
        if (reason !== 'backdropClick') {
            setComment('');
            type.current = null;
            closeComment(e, reason);
        }
    };

    const handleChangeText = (e) => {
        const {value, name} = e.target;
        setForm(prevState => ({
            ...prevState,
            [name]: value,
        }));
        setSaveData(true);
    };

    const handleChangeDict = (name, value) => {
        setForm(prevState => ({
            ...prevState,
            [name]: value,
        }));
        setSaveData(true);
    };

    const renderComment = () => {
        const handleCommentChange = (e) => {
            const {value} = e.target;
            setComment(value);
        };

        return (
            <>
                <div>
                    <FormLabel>
                        {t('Text')}
                    </FormLabel>
                </div>

                <AgyleTextField
                    debounce
                    name="comment"
                    value={comment}
                    onChange={handleCommentChange}
                    size="large"
                    noBorder={false}
                    withNewBackground
                    fullWidth
                />
            </>
        );
    };

    const renderGroup = (field, label, clinician, commentLabel, nullStateSwitch, withinHour, noAsNull, customLabel) => {
        const handleChangeSwitch = (val) => {
            let clone = _.cloneDeep(form);
            clone[field] = val;
            addUserChange(clone, field);

            if (clinician) {
                if (_.isNull(val) || (!nullStateSwitch && val === false)) {
                    clone[`${field}Who`] = null;
                    clone[`${field}When`] = null;
                } else {
                    if (!clone[`${field}Who`]) {
                        const clinicianUser = _.find(nursesCliniciansList, {userName: userObject?.userName});
                        clone[`${field}Who`] = clinicianUser ? clinicianUser : null;
                    }
                    if (!clone[`${field}When`]) clone[`${field}When`] = moment().toISOString();
                }
            }

            // Add time to track
            if (withinHour) {
                clone.withinHourTimesToTrack = val === true ? moment().add(1, 'hour').toISOString() : null;
            }

            setForm(clone);
            setSaveData(true);
        };

        const handleOpenComment = () => {
            type.current = field;
            setComment(form[`${field}Note`]);
            openComment();
        };

        const handleChangeClinician = (_e, val) => {
            let clone = _.cloneDeep(form);
            clone[`${field}Who`] = val;
            addUserChange(clone, field);
            setForm(clone);
            setSaveData(true);
        };

        const handleChangeDate = (val) => {
            let clone = _.cloneDeep(form);
            clone[`${field}When`] = moment(val).toISOString();
            addUserChange(clone, field);
            setForm(clone);
            setSaveData(true);
        };

        const getConsultantValue = () => {
            if (form[`${field}Who`]) {
                return form[`${field}Who`];
            } else {
                const clinicianUser = _.find(cliniciansDict, {userName: userObject?.userName});
                return clinicianUser ? clinicianUser : null;
            }
        };

        const getConsultantName = (value) => {
            if (!value) return '';

            if (config.consultantComponentEnabled) {
                const familyName = _.capitalize(value.familyName);
                const firstName = _.capitalize(value.firstName);

                return `${familyName}, ${firstName}`;
            } else {
                return value?.__description;
            }
        };

        const getFilteredCliniciansDict = () => {
            let clinicians = [];

            if (!cliniciansDict || _.isEmpty(cliniciansDict)) return [];

            cliniciansDict.forEach(clinician => {
                if (clinician.availableIn.includes('bedBooking')) {
                    clinicians.push(clinician);
                }
            });
            return clinicians;
        };

        return (
            <>
                <div className={classes.groupRoot}>
                    <Grid container direction="row" alignItems="center" justifyContent="space-between">
                        <FormLabel>
                            {t(label)}
                        </FormLabel>

                        <div className={classes.centerRow}>
                            <AgyleBlockSwitch
                                onChange={handleChangeSwitch}
                                value={form[field]}
                                nullState={nullStateSwitch}
                                style={{marginRight: 10}}
                                customLabels={(!nullStateSwitch && !noAsNull) ? {no: t('N/R')} : null}
                            />

                            <div style={{margin: '0 5px'}}/>

                            <AgyleButton
                                size="small"
                                onClick={handleOpenComment}
                                variant="contained"
                                startIcon={<AddIcon/>}
                                width="160px"
                            >
                                {t(commentLabel || 'Comment')}
                            </AgyleButton>
                        </div>
                    </Grid>
                    {!_.isEmpty(form[`${field}User`]) &&
                        <div className={classes.userLog}>
                            {`${form[`${field}User`]} ${moment(form[`${field}Timestamp`]).format(config.defaultDateTimeFormat)}`}
                        </div>
                    }
                    {!_.isEmpty(form[`${field}Note`]) &&

                        <div className={classes.comment}>
                            {form[`${field}Note`]}
                        </div>
                    }
                </div>
                {clinician &&
                    <Grid container direction="row" className={classes.clinician} spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <AgyleDictionaryField
                                label={`${label} Clinician`}
                                onChange={handleChangeClinician}
                                //options={nursesCliniciansList}
                                groupBy={opt => config.consultantComponentEnabled? "" : (config?.dropdownsWithCliniciansAndNurses ? opt.group : "")}
                                //value={form[`${field}Who`]}
								options={config.consultantComponentEnabled ? getFilteredCliniciansDict() : nursesCliniciansList}
                                getOptionLabel={(option) => getConsultantName(option)}
                                value={config.consultantComponentEnabled ? getConsultantValue() : form[`${field}Who`]}
                                loading={cliniciansLoading}
                                disabled={!form[field]}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <AgyleDatePicker
                                label={`${label} Date and Time`}
                                onChange={handleChangeDate}
                                value={form[`${field}When`]}
                                type="dateTime"
                                disabled={!form[field]}
                                maxDate={moment()}
                                minDate={calculateArrivalDate(props.patient)}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    </Grid>
                }
            </>
        );
    };

    const renderAnotherDestination = () => {
        const handleChangeSwitch = (val) => {
            let clone = _.cloneDeep(form);
            clone.anotherDestination = val;
            addUserChange(clone, 'anotherDestination');
            setForm(clone);
            setSaveData(true);
        };

        const handleOpenComment = () => {
            type.current = 'anotherDestination';
            setComment(form[`anotherDestinationNote`]);
            openComment();
        };

        return (
            <>
                <div className={classes.groupRoot}>
                    <Grid container direction="row" alignItems="center" justifyContent="space-between">
                        <FormLabel>
                            {t('Site bed is requested at')}
                        </FormLabel>

                        <div className={classes.centerRow}>
                            <AgyleBlockSwitch
                                onChange={handleChangeSwitch}
                                value={form.anotherDestination}
                                style={{marginRight: 10}}
                                customLabels={{yes: t('Poole'), no: t('Bournemouth')}}
                                valueObject={{'true': 'Poole', 'false': 'Bournemouth'}}
                            />

                            <div style={{margin: '0 5px'}}/>

                            <AgyleButton
                                size="small"
                                onClick={handleOpenComment}
                                variant="contained"
                                startIcon={<AddIcon/>}
                                width="160px"
                            >
                                {t('Comment')}
                            </AgyleButton>
                        </div>
                    </Grid>

                    {!_.isEmpty(form[`anotherDestinationUser`]) &&
                        <div className={classes.userLog}>
                            {`${form[`anotherDestinationUser`]} ${moment(form[`anotherDestinationTimestamp`]).format(config.defaultDateTimeFormat)}`}
                        </div>
                    }

                    {!_.isEmpty(form[`anotherDestinationNote`]) &&
                        <div className={classes.comment}>
                            {form[`anotherDestinationNote`]}
                        </div>
                    }
                </div>
            </>
        );
    };

    return (
        <>
            <>
                {renderGroup('requested', 'Requested', true, 'ED Note')}

                <Divider className={classes.divider}/>

                <div className={classes.row}>
                    <AgyleDictionaryField
                        label={t('ECDS Activity Treatment')}
                        onChange={(_e, val) => handleChangeDict('activityTreatment', val)}
                        options={activityTreatment}
                        value={form.activityTreatment}
                        loading={activityTreatmentLoading}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </div>

                <div className={classes.row}>
                    <AgyleTextField
                        debounce
                        label={t('Reason for Admissions')}
                        name="reasonForAdmissions"
                        value={form.reasonForAdmissions}
                        onChange={handleChangeText}
                        fullWidth
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </div>

                <Divider className={classes.divider}/>

                {renderGroup('onHoldWard', 'On Hold Ward', false, 'Comment', false, false, true)}
                {renderGroup('sideRoom', 'Side Room', false, 'Reason')}
                {renderGroup('patientPost', 'Patient Post Take Complete', false, 'Comment', false, false, true)}

                <Divider className={classes.divider}/>

                {config?.modules?.forms?.bedBooking?.enableAnotherDestination &&
                    <>
                        {renderAnotherDestination()}
                        <Divider className={classes.divider}/>
                    </>
                }

                {renderGroup('allocated', 'Allocated', true, 'WARD Feedback')}
                {renderGroup('withinHour', 'Within an hour', false, 'Comment', true, true)}

                <Divider className={classes.divider}/>

                <div className={classes.row}>
                    <AgyleDictionaryField
                        label={t('Ward')}
                        onChange={(_e, val) => handleChangeDict('ward', val)}
                        options={wards}
                        value={form.ward}
                        getOptionLabel={option => option.wardName}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </div>

                <Divider className={classes.divider}/>

                {renderGroup('ready', 'Ready', true, 'Comment')}

                <Divider className={classes.divider}/>

                {renderGroup('canceled', 'Canceled', true, 'Comment')}
            </>

            <MessageBox
                open={isClearConfirmOpen}
                title={t('CAUTION')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={handleClearData}
                onClose={closeClearConfirm}
                message={t('Are you sure you want delete all recorded values?')}
                warning
            />

            <MessageBox
                open={isCommentOpen}
                title={t('Add Comment')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleAddComment}
                onClose={handleCloseComment}
                messageObject={renderComment()}
                fullWidth
            />
        </>
    );
};

export default BedRequestForm;
